import TreasuryTable from './TreasuryTable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAPI } from '../../utils/utils';
import './PayoutsTab.css';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const formatCurrency = (amount, currency = 'eur') => {
    const normalizedCurrency = (currency || 'eur').toUpperCase();
    try {
        return new Intl.NumberFormat('ca-ES', {
            style: 'currency',
            currency: normalizedCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(amount) || 0);
    } catch (error) {
        return `${(Number(amount) || 0).toFixed(2).replace('.', ',')} €`;
    }
};

const toDate = (value, unixSeconds = false) => {
    if (value === null || value === undefined) return null;
    const date = unixSeconds
        ? new Date((Number(value) || 0) * 1000)
        : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatArrivalDate = (arrivalTimestamp) => {
    const date = toDate(arrivalTimestamp, true);
    if (!date) return '-';

    return date.toLocaleDateString('ca-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
};

const formatShortDate = (value, unixSeconds = false) => {
    const date = toDate(value, unixSeconds);
    if (!date) return '-';

    return date.toLocaleDateString('ca-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

const formatDateTime = (value, unixSeconds = false) => {
    const date = toDate(value, unixSeconds);
    if (!date) return '-';

    return date.toLocaleString('ca-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const getDaysUntilArrival = (arrivalTimestamp) => {
    const date = toDate(arrivalTimestamp, true);
    if (!date) return null;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfArrival = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    return Math.round((startOfArrival.getTime() - startOfToday.getTime()) / DAY_IN_MS);
};

const getDaysLabel = (days) => {
    if (days === null || days === undefined) return '-';
    if (days <= 0) return 'Avui';
    if (days === 1) return 'Demà';
    return `En ${days} dies`;
};

const formatPaymentCount = (count) => {
    const total = Number(count) || 0;
    return `${total} ${total === 1 ? 'pagament' : 'pagaments'}`;
};

const getStatusLabel = (status) => {
    if (status === 'in_transit') return 'En trànsit';
    if (status === 'pending') return 'Pendent';
    if (status === 'paid') return 'Rebut';
    if (status === 'failed') return 'Error';
    if (status === 'canceled') return 'Cancel·lat';
    return status || 'Desconegut';
};

const getStatusClass = (status) => {
    if (status === 'in_transit') return 'in-transit';
    if (status === 'pending') return 'pending';
    if (status === 'paid') return 'paid';
    if (status === 'failed') return 'failed';
    if (status === 'canceled') return 'canceled';
    return 'other';
};

const SKELETON_SECTIONS = [
    { key: 'upcoming', columns: 6, rows: 2 },
    { key: 'history', columns: 5, rows: 3 }
];

function PayoutsSkeleton() {
    return (
        <div className="payouts-skeleton" aria-hidden="true">
            {SKELETON_SECTIONS.map((section) => (
                <div key={section.key} className="payouts-section">
                    <div className="payouts-section-header">
                        <div className="payouts-skeleton-block payouts-skeleton-title" />
                        <div className="payouts-skeleton-block payouts-skeleton-count" />
                    </div>
                    <div className="payouts-table-wrapper">
                        <table className="payouts-table">
                            <thead>
                                <tr>
                                    {Array.from({ length: section.columns }).map((_, i) => (
                                        <th key={i}>
                                            <div className="payouts-skeleton-block payouts-skeleton-th" />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: section.rows }).map((_, row) => (
                                    <tr key={row}>
                                        {Array.from({ length: section.columns }).map((_, col) => (
                                            <td key={col}>
                                                <div className="payouts-skeleton-block payouts-skeleton-cell" />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
}

const getFailedBatches = (attempt) => (Array.isArray(attempt?.failedBatches) ? attempt.failedBatches : []);

// A partial run can still have buckets Stripe rejected; those need explaining too.
const hasFailedAttempt = (attempt) => attempt?.status === 'failed' || getFailedBatches(attempt).length > 0;

// Shown while the latest automatic payout run failed, so pending money does not sit unexplained.
function PayoutsFailedAttempt({ attempt }) {
    const failedBatches = getFailedBatches(attempt);

    return (
        <div className="payouts-alert" role="alert">
            <p className="payouts-alert-title">
                {attempt.status === 'failed'
                    ? 'L\'última transferència automàtica no s\'ha pogut fer'
                    : 'Part de l\'última transferència automàtica no s\'ha pogut fer'}
            </p>
            <p className="payouts-alert-meta">
                {formatDateTime(attempt.startedAt)} · Es tornarà a provar automàticament cada nit.
            </p>
            <ul className="payouts-alert-reasons">
                {attempt.errorMessage ? (
                    <li>{attempt.errorMessage}</li>
                ) : null}
                {failedBatches.map((batch, index) => (
                    <li key={`${batch.bucketType}:${batch.bucketId}:${index}`}>
                        <strong>{batch.bucketLabel || batch.bucketId}</strong> ({formatCurrency(batch.amount)}): {batch.errorMessage || 'Error desconegut'}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function PayoutsTab({ stripeConnectStatus, onChangeTab }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [overview, setOverview] = useState(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [blockedSummary, setBlockedSummary] = useState(null);

    const fetchPayoutOverview = useCallback(() => {
        setLoading(true);
        setError(null);

        fetchAPI('/stripe/connect/payouts/overview', (data) => {
            if (data?.success) {
                setOverview(data);
                setLastUpdatedAt(new Date());
                setError(null);
            } else {
                setOverview(null);
                setError(data?.message || 'No s\'ha pogut carregar les transferències.');
            }
            setLoading(false);
        }, false, () => {
            // fetchAPI only calls this on HTTP/network errors; without it the tab stays loading forever.
            setOverview(null);
            setError('No s\'ha pogut carregar les transferències.');
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (stripeConnectStatus && !stripeConnectStatus.verified) {
            setLoading(false);
            setError(null);
            setOverview(null);
            setBlockedSummary(null);
            // Money may already be pending from before Stripe disabled payouts.
            fetchAPI('/stripe/connect/payouts/summary', (data) => {
                setBlockedSummary(data?.success ? data : null);
            }, false, () => setBlockedSummary(null));
            return;
        }

        fetchPayoutOverview();
    }, [fetchPayoutOverview, stripeConnectStatus]);

    const confirmedUpcoming = useMemo(
        () => overview?.confirmed?.upcoming || [],
        [overview]
    );
    const confirmedHistory = useMemo(
        () => overview?.confirmed?.history || [],
        [overview]
    );
    const estimatedPendingBuckets = useMemo(
        () => overview?.estimated?.pending?.buckets || [],
        [overview]
    );

    const summary = useMemo(() => {
        const upcomingTotalAmount = overview?.confirmed?.summary?.upcomingTotalAmount
            ?? confirmedUpcoming.reduce((sum, payout) => sum + (Number(payout.amount) || 0), 0);
        const historyPaidTotalAmount = overview?.confirmed?.summary?.historyPaidTotalAmount
            ?? confirmedHistory
                .filter((payout) => payout.status === 'paid')
                .reduce((sum, payout) => sum + (Number(payout.amount) || 0), 0);
        const pendingEstimatedAmount = overview?.estimated?.pending?.totalAmount
            ?? estimatedPendingBuckets.reduce((sum, bucket) => sum + (Number(bucket.amount) || 0), 0);

        return {
            upcomingCount: overview?.confirmed?.summary?.upcomingCount ?? confirmedUpcoming.length,
            upcomingTotalAmount,
            historyCount: overview?.confirmed?.summary?.historyCount ?? confirmedHistory.length,
            historyPaidTotalAmount,
            pendingEstimatedAmount,
            // Only set when a pending bucket can actually be paid out.
            nextEligibleRunAt: overview?.estimated?.estimatedPayoutCreationAt || null,
            estimatedArrivalAt: overview?.estimated?.estimatedArrivalAt || null
        };
    }, [overview, confirmedUpcoming, confirmedHistory, estimatedPendingBuckets]);

    const lastAttempt = overview?.estimated?.lastAttempt || null;

    // Only the first load shows skeletons; a refresh keeps the current data visible.
    const showSkeleton = loading && !overview;

    if (stripeConnectStatus && !stripeConnectStatus.verified) {
        return (
            <div className="payouts-tab-section">
                <div className="payouts-header">
                    <div>
                        <h2>Transferències al banc</h2>
                        <p className="payouts-description">
                            Per veure les transferències, primer cal activar Stripe Connect.
                        </p>
                    </div>
                </div>

                {blockedSummary?.pending?.totalAmount > 0 ? (
                    <div className="payouts-alert" role="alert">
                        <p className="payouts-alert-title">
                            Hi ha {formatCurrency(blockedSummary.pending.totalAmount)} pendents de transferir al banc
                        </p>
                        <p className="payouts-alert-meta">
                            {formatPaymentCount(blockedSummary.pending.totalPayments)}. No es podran transferir fins que Stripe Connect estigui verificat.
                        </p>
                        {blockedSummary.lastAttempt?.errorMessage ? (
                            <ul className="payouts-alert-reasons">
                                <li>{blockedSummary.lastAttempt.errorMessage}</li>
                            </ul>
                        ) : null}
                    </div>
                ) : null}

                <div className="payouts-empty-state">
                    <p>Stripe Connect encara no està verificat.</p>
                    {typeof onChangeTab === 'function' ? (
                        <button
                            className="payouts-primary-button"
                            onClick={() => onChangeTab('stripe')}
                        >
                            Anar a Pagaments
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div className="payouts-tab-section">
            <div className="payouts-header">
                <div>
                    <h2>Transferències al banc</h2>
                    <p className="payouts-description">
                        Resum dels diners transferits al vostre compte bancari des de Stripe.
                    </p>
                    {lastUpdatedAt ? (
                        <p className="payouts-last-updated">
                            Actualitzat: {lastUpdatedAt.toLocaleString('ca-ES')}
                        </p>
                    ) : null}
                </div>

                <button
                    className="payouts-refresh-button"
                    onClick={fetchPayoutOverview}
                    disabled={loading}
                >
                    {loading ? 'Actualitzant...' : 'Actualitzar'}
                </button>
            </div>

            <div className="payouts-summary-grid" aria-busy={showSkeleton}>
                {[
                    { label: 'En camí al banc', amount: summary.upcomingTotalAmount },
                    { label: 'Pendent de transferir', amount: summary.pendingEstimatedAmount },
                    { label: 'Total rebut', amount: summary.historyPaidTotalAmount }
                ].map((card) => (
                    <div key={card.label} className="payouts-summary-card">
                        {showSkeleton ? (
                            <span className="payouts-skeleton-block payouts-skeleton-value" />
                        ) : (
                            <span className="payouts-summary-value">{formatCurrency(card.amount)}</span>
                        )}
                        <span className="payouts-summary-label">{card.label}</span>
                    </div>
                ))}
            </div>

            {showSkeleton ? (
                <PayoutsSkeleton />
            ) : error ? (
                <div className="payouts-error">
                    <p>{error}</p>
                    <button className="payouts-primary-button" onClick={fetchPayoutOverview}>
                        Tornar-ho a provar
                    </button>
                </div>
            ) : (
                <>
                    {hasFailedAttempt(lastAttempt) ? (
                        <PayoutsFailedAttempt attempt={lastAttempt} />
                    ) : null}

                    {(summary.nextEligibleRunAt || summary.estimatedArrivalAt) ? (
                        <div className="payouts-banner">
                            {summary.nextEligibleRunAt ? (
                                <div>
                                    <span className="payouts-banner-label">Propera transferència estimada</span>
                                    <span className="payouts-banner-value">{formatDateTime(summary.nextEligibleRunAt)}</span>
                                </div>
                            ) : null}
                            {summary.estimatedArrivalAt ? (
                                <div>
                                    <span className="payouts-banner-label">Arribada estimada al banc</span>
                                    <span className="payouts-banner-value">{formatDateTime(summary.estimatedArrivalAt)}</span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="payouts-section">
                        <div className="payouts-section-header">
                            <h3>En camí al banc</h3>
                            <span>{confirmedUpcoming.length}</span>
                        </div>
                        {confirmedUpcoming.length === 0 ? (
                            <div className="payouts-empty-state">
                                <p>No hi ha cap transferència en camí ara mateix.</p>
                            </div>
                        ) : (
                            <TreasuryTable label="En camí al banc" className="payouts-table-wrapper">
                                <table className="payouts-table">
                                    <thead>
                                        <tr>
                                            <th>Tipus</th>
                                            <th>Import</th>
                                            <th>Estat</th>
                                            <th>Data</th>
                                            <th>Arribada prevista</th>
                                            <th>Falten</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {confirmedUpcoming.map((payout) => {
                                            const daysUntil = getDaysUntilArrival(payout.arrival_date);
                                            return (
                                                <tr key={payout.id}>
                                                    <td>{payout.bucketLabel || '-'}</td>
                                                    <td className="payout-amount">{formatCurrency(payout.amount, payout.currency)}</td>
                                                    <td>
                                                        <span className={`payout-status ${getStatusClass(payout.status)}`}>
                                                            {getStatusLabel(payout.status)}
                                                        </span>
                                                    </td>
                                                    <td>{formatShortDate(payout.created, true)}</td>
                                                    <td>{formatArrivalDate(payout.arrival_date)}</td>
                                                    <td>
                                                        <span className={`payout-days ${daysUntil !== null && daysUntil <= 1 ? 'soon' : ''}`}>
                                                            {getDaysLabel(daysUntil)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </TreasuryTable>
                        )}
                    </div>

                    <div className="payouts-section">
                        <div className="payouts-section-header">
                            <h3>Historial de transferències</h3>
                            <span>{confirmedHistory.length}</span>
                        </div>
                        {confirmedHistory.length === 0 ? (
                            <div className="payouts-empty-state">
                                <p>Encara no s'ha fet cap transferència.</p>
                            </div>
                        ) : (
                            <TreasuryTable label="Historial de transferències" className="payouts-table-wrapper">
                                <table className="payouts-table">
                                    <thead>
                                        <tr>
                                            <th>Tipus</th>
                                            <th>Import</th>
                                            <th>Estat</th>
                                            <th>Data</th>
                                            <th>Arribada al banc</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {confirmedHistory.map((payout) => (
                                            <tr key={payout.id}>
                                                <td>{payout.bucketLabel || '-'}</td>
                                                <td className="payout-amount">{formatCurrency(payout.amount, payout.currency)}</td>
                                                <td>
                                                    <span className={`payout-status ${getStatusClass(payout.status)}`}>
                                                        {getStatusLabel(payout.status)}
                                                    </span>
                                                </td>
                                                <td>{formatShortDate(payout.created, true)}</td>
                                                <td>{formatArrivalDate(payout.arrival_date)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </TreasuryTable>
                        )}
                    </div>

                    <div className="payouts-section">
                        <div className="payouts-section-header">
                            <h3>Pagaments pendents de transferir</h3>
                            <span>{formatPaymentCount(overview?.estimated?.pending?.totalPayments)}</span>
                        </div>
                        <p className="payouts-section-note">
                            Pagaments rebuts que encara no s'han inclòs en una transferència al banc.
                        </p>
                        {overview?.estimated?.warning ? (
                            <p className="payouts-warning">
                                {overview.estimated.warning}
                            </p>
                        ) : null}

                        {estimatedPendingBuckets.length === 0 ? (
                            <div className="payouts-empty-state">
                                <p>No hi ha pagaments pendents de transferir.</p>
                            </div>
                        ) : (
                            <TreasuryTable label="Pagaments pendents de transferir" className="payouts-table-wrapper">
                                <table className="payouts-table">
                                    <thead>
                                        <tr>
                                            <th>Tipus</th>
                                            <th>Pagaments</th>
                                            <th>Import</th>
                                            <th>Des de</th>
                                            <th>Fins a</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {estimatedPendingBuckets.map((bucket) => (
                                            <tr key={bucket.bucketKey}>
                                                <td>
                                                    {bucket.bucketLabel}
                                                    {bucket.belowMinimum ? (
                                                        <span className="payouts-bucket-note">
                                                            Per sota del mínim d'1 € de Stripe: s'enviarà quan n'hi hagi més.
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td>{bucket.paymentCount}</td>
                                                <td className="payout-amount">{formatCurrency(bucket.amount)}</td>
                                                <td>{formatShortDate(bucket.oldestPaidAt)}</td>
                                                <td>{formatShortDate(bucket.newestPaidAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </TreasuryTable>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default PayoutsTab;
